import { PaginatedDataDTO, PaginationDTO, PaginationRestDTO } from '@bottega52/commons-pagination';
import { setLanguageWithMoment } from '../../translations/utils';
import { IState } from '../store';
import { ActionsUnion, createAction, IThunkAction } from '../utils';
import SettingsActionTypesEnum from './model/SettingsActionTypesEnum';
import * as LuckeySofiaSiteAPI from '../../repository/jago/LuckeySofiaSiteAPI';
import * as ParamConfigAPI from '../../repository/jago/paramConfigAPI';
import _ from 'lodash';
import { decodeUnicode } from '../../utils/Utils';
import moment from 'moment';
import { IParamConfigurationInDTO } from '../../repository/jago/model/input/IParamConfigurationInDTO';
import { IParamConfigurationCreateOutDTO, IParamConfigurationUpdateOutDTO } from '../../repository/jago/model/output/IParamConfigurationOutDTO';

function convertPaginationData<T>(data: PaginationRestDTO<T>): PaginatedDataDTO<T> {
  const pagination: PaginationDTO = _.omit(data, 'content');
  return {
    content: data.content,
    pagination,
  };
}

export const SettingsActions = {
  setLanguage: createAction<typeof SettingsActionTypesEnum.SET_LANGUAGE, string>(SettingsActionTypesEnum.SET_LANGUAGE),
  setSpinnerVisible: createAction<typeof SettingsActionTypesEnum.SET_SPINNER_VISIBLE, boolean>(SettingsActionTypesEnum.SET_SPINNER_VISIBLE),
  saveWhatsNewContent: createAction<typeof SettingsActionTypesEnum.SAVE_WHATSNEW_CONTENT, any[]>(SettingsActionTypesEnum.SAVE_WHATSNEW_CONTENT),
  saveWhatsNewHasUpdate: createAction<typeof SettingsActionTypesEnum.SAVE_WHATSNEW_HASUPDATE, boolean>(SettingsActionTypesEnum.SAVE_WHATSNEW_HASUPDATE),
  saveParamConfigs: createAction<typeof SettingsActionTypesEnum.SAVE_PARAM_CONFIGS, PaginatedDataDTO<IParamConfigurationInDTO>>(SettingsActionTypesEnum.SAVE_PARAM_CONFIGS),
  setSelectedParamConfig: createAction<typeof SettingsActionTypesEnum.SET_SELECTED_PARAM_CONFIG, IParamConfigurationInDTO | null>(SettingsActionTypesEnum.SET_SELECTED_PARAM_CONFIG),
  saveParamPublicConfiguration: createAction<typeof SettingsActionTypesEnum.SAVE_PARAM_PUBLIC_CONFIGURATION, IParamConfigurationInDTO[]>(SettingsActionTypesEnum.SAVE_PARAM_PUBLIC_CONFIGURATION),
};

export function saveLanguageSettings(language: string): IThunkAction<void, IState> {
  return (dispatch, getState) => {
    setLanguageWithMoment(language);
    dispatch(SettingsActions.setLanguage(language));
  };
}

export function setSpinnerVisible(isVisible: boolean): IThunkAction<void, IState> {
  return (dispatch, getState) => {
    dispatch(SettingsActions.setSpinnerVisible(isVisible));
  };
}

export function resetWhatsNewHasUpdate(): IThunkAction<void, IState> {
  return (dispatch, getState) => {
    dispatch(SettingsActions.saveWhatsNewHasUpdate(false));
  };
}

export function fetchWhatsNewArticles() {
  return async (dispatch, getState) => {
    try {
      const languagePrefix = "en"
      const content = await LuckeySofiaSiteAPI.fetchWhatsNewArticles(languagePrefix);
      if (content && content.data) {
        const whatsNewItemsContent = content.data;
        const whatsNewArticles = _.map(whatsNewItemsContent, (itemContent) => {
          const previewImage = itemContent && itemContent['_embedded'] && itemContent['_embedded']['wp:featuredmedia'] && itemContent['_embedded']['wp:featuredmedia'][0] ? itemContent['_embedded']['wp:featuredmedia'][0].source_url : null;
          return {
            ...itemContent,
            preview: itemContent && itemContent.excerpt && itemContent.excerpt.rendered ? itemContent.excerpt.rendered : undefined,
            previewImage,
            renderedTitle: itemContent && itemContent.title && itemContent.title.rendered ? decodeUnicode(itemContent.title.rendered) : '',
            renderedContent: itemContent && itemContent.content && itemContent.content.rendered ? itemContent.content.rendered : '',
          };
        });
        dispatch(SettingsActions.saveWhatsNewContent(whatsNewArticles));
        const lastUpdate = _.first(whatsNewArticles) && _.first(whatsNewArticles).date;
        if (lastUpdate) {
          const whatNewStoredLastUpdate = localStorage.getItem('lastWhatsNewUpdate');
          const mustRead = whatNewStoredLastUpdate === 'undefined' || whatNewStoredLastUpdate === null|| moment(lastUpdate).isAfter(whatNewStoredLastUpdate);
          dispatch(SettingsActions.saveWhatsNewHasUpdate(mustRead));
        }
        return content;
      }
      return [];
    } catch (error) {
      return [];
    }
  };
}

export function fetchParamConfigurations(params?: ParamConfigAPI.IParamConfigurationRequestParams): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await ParamConfigAPI.fetchParamConfigurations(params);
      if (response && response.data) {
        const paginatedData = convertPaginationData(response.data);
        dispatch(SettingsActions.saveParamConfigs(paginatedData));
        return paginatedData;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchParamPublicConfiguration(): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await ParamConfigAPI.fetchParamPublicConfiguration();
      if (response && response.data) {
        dispatch(SettingsActions.saveParamPublicConfiguration(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchParamConfiguration(id: number): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await ParamConfigAPI.fetchParamConfiguration(id);
      if (response && response.data) {
        dispatch(SettingsActions.setSelectedParamConfig(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function createParamConfiguration(data: IParamConfigurationCreateOutDTO): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await ParamConfigAPI.createParamConfiguration(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateParamConfiguration(id: number, data: IParamConfigurationUpdateOutDTO): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      const response = await ParamConfigAPI.updateParamConfiguration(id, data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function deleteParamConfiguration(id: number): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    try {
      await ParamConfigAPI.deleteParamConfiguration(id);
    } catch (error) {
      throw error;
    }
  };
}

export function saveParamConfigurationByKey(keyConfig: string, valueConfig: string): IThunkAction<void, IState> {
  return async (dispatch, getState) => {
    await dispatch(fetchParamConfigurations({ page: 0, pageSize: 500 }));
    const { paramConfigs } = getState().settings;
    const existingConfig = _.find(paramConfigs.data.content, { keyConfig });
    if (existingConfig) {
      await dispatch(updateParamConfiguration(existingConfig.id, { valueConfig }));
    } else {
      await dispatch(createParamConfiguration({ keyConfig, valueConfig }));
    }
    await dispatch(fetchParamConfigurations({ page: 0, pageSize: 500 }));
  };
}

export type SettingsActionsType = ActionsUnion<typeof SettingsActions>;
